import React from "react";

import DeleteIcon from "calcite-ui-icons-react/XIcon";
import Tabs, { TabContents, TabSection } from "calcite-react/Tabs";

import { ListItem, ListItemTitle } from "../../styles/Common/list";
import { TabNav, TabTitle } from "../../styles/appStyles";

import { AreaStats } from "../../components/stats";
import Button from "../../../common/components/Button";
import Context from "../../../context/Context";
import FieldNames from "../../../aiim/datasets/FieldNames";
import Items from "../common/Items";
import { ModalController } from "../../../common/components/Modal";
import OfficePlan from "../../base/OfficePlan";
import PersonPopup from "../common/PersonPopup";
import QueryCriteria from "../../base/QueryCriteria";
import Topic from "../../../context/Topic";
import TransactionGuard from "../../base/TransactionGuard";
import UnassignPrompt from "../common/UnassignPrompt";
import UnitPopup from "../common/UnitPopup";
import * as aiimUtil from "../../../aiim/util/aiimUtil";
import * as component from "../../../components/util/component";
import * as officePlanUtil from "../../base/officePlanUtil";
import * as queryUtil from "../../base/queryUtil";
import * as sourceUtil from "../../base/sourceUtil";
import * as transactions from "../../base/transaction/transactions";

class PeopleAndUnits extends React.Component {
  componentId;
  peopleQueryCriteriaKey;
  unitsQueryCriteriaKey;

  _mounted = false;

  constructor(props) {
    super(props);
    const i18n = Context.instance.i18n;
    this.componentId = component.nextId("qc");
    this.peopleQueryCriteriaKey = this.componentId + "-people";
    this.unitsQueryCriteriaKey = this.componentId + "-units";
    this.initPeopleQueryCriteria();
    this.initUnitsQueryCriteria();

    this.state = component.newState({
      activeTabIndex: 0,
      peopleAssigned: null,
      unitsAssigned: null,
      peopleWereQueried: false,
      unitsWereQueried: false,
      peopleLabel: i18n.spaceplanner.people.people,
      unitsLabel: i18n.spaceplanner.units.units,
    });
  }

  componentDidMount() {
    this._mounted = true;
    this.queryUnitCount();

    component.own(this, [
      Topic.subscribe(Topic.ListCountInfo, (params) => {
        if (params && typeof params.count === "number") {
          if (this.peopleQueryCriteriaKey === params.queryCriteriaKey) {
            const i18n = Context.instance.i18n;
            let v = i18n.spaceplanner.people.peopleCountPattern;
            v = v.replace("{count}", params.count);
            if (this._mounted) this.setState({ peopleLabel: v });
          } else if (this.unitsQueryCriteriaKey === params.queryCriteriaKey) {
            const i18n = Context.instance.i18n;
            let v = i18n.spaceplanner.units.unitsCountPattern;
            v = v.replace("{count}", params.count);
            if (this._mounted) this.setState({ unitsLabel: v });
          }
        }
      }),
      Topic.subscribe(Topic.PlanModified, (params) => {
        const actions = [OfficePlan.Action_AssignmentsUpdated];
        if (actions && actions.indexOf(params.action) !== -1) {
          if (this.state.activeTabIndex === 0) {
            this.queryUnitCount();
          } else {
            this.queryPeopleCount();
          }
        }
      }),
    ]);
  }

  componentWillUnmount() {
    const session = Context.instance.session;
    if (session.queryCriteriaByKey) {
      delete session.queryCriteriaByKey[this.peopleQueryCriteriaKey];
      delete session.queryCriteriaByKey[this.unitsQueryCriteriaKey];
    }
    component.componentWillUnmount(this);
    this._mounted = false;
  }

  confirmDelete = (evt, item, type, index) => {
    if (evt) evt.stopPropagation();
    const i18n = Context.instance.i18n;
    const promptOptions = {
      subject: type, // person or unit
      subjectItem: item,
      from: this.props.areaType,
      fromItem: this.props.areaItem,
    };
    const content = <UnassignPrompt promptOptions={promptOptions} />;
    const controller = new ModalController({});
    let okButton;
    let props = {
      title: i18n.spaceplanner.unassignPrompt.removeAssignmentTitle,
      showOKCancel: true,
      closeOnOK: true,
      okLabel: i18n.spaceplanner.unassign,
      onOK: () => {
        if (okButton) okButton.disable();
        if (type === "unit") this.deleteUnit(item, index);
        if (type === "person") this.deletePerson(item, index);
      },
      onMountOKButton: (btn) => {
        okButton = btn;
      },
    };
    controller.show(props, content);
  };

  deletePerson = (personItem, index) => {
    const guard = new TransactionGuard({force: true});
    transactions
      .unassignPeople([personItem])
      .then((result) => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated,
          index: index,
          queryCriteriaKey: this.peopleQueryCriteriaKey
        });
      })
      .catch((error) => {
        guard.close();
        console.error("Error unassigning people", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  deleteUnit = (unitItem, index) => {
    const guard = new TransactionGuard({force: true});
    transactions
      .unassignUnits([unitItem])
      .then((result) => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated,
          index: index,
          queryCriteriaKey: this.unitsQueryCriteriaKey
        });
      })
      .catch((error) => {
        guard.close();
        console.error("Error unassigning units", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  initPeopleQueryCriteria = () => {
    const source = sourceUtil.getPeopleSource();
    const fields = source.layer2D.fields;
    let nameFieldName = FieldNames.PEOPLE_FULLNAME;
    const nameField = aiimUtil.findField(fields, nameFieldName);
    if (nameField) nameFieldName = nameField.name;
    const queryCriteriaKey = this.peopleQueryCriteriaKey;
    const queryCriteria = QueryCriteria.get(queryCriteriaKey, source.key);
    queryCriteria.requiresGeometry = true;
    if (!queryCriteria.sortOptions) {
      queryCriteria.primarySortField = nameFieldName;
      queryCriteria.sortOptions = {
        sortBy: queryCriteria.primarySortField,
        sortDir: "asc",
      };
    }
    const areaItem = this.props.areaItem;
    const areaType = this.props.areaType;
    const where = officePlanUtil.makeWhereForPeopleAssignedToArea(
      areaItem,
      areaType
    );
    queryCriteria.where = where;
  };

  initUnitsQueryCriteria = () => {
    const source = sourceUtil.getUnitsSource();
    const fields = source.layer2D.fields;
    let nameFieldName = FieldNames.NAME;
    const nameField = aiimUtil.findField(fields, nameFieldName);
    if (nameField) nameFieldName = nameField.name;
    const queryCriteriaKey = this.unitsQueryCriteriaKey;
    const queryCriteria = QueryCriteria.get(queryCriteriaKey, source.key);
    queryCriteria.requiresGeometry = true;
    //queryCriteria.num = 50;
    if (!queryCriteria.sortOptions) {
      queryCriteria.primarySortField = nameFieldName;
      queryCriteria.sortOptions = {
        sortBy: queryCriteria.primarySortField,
        sortDir: "asc",
      };
    }
    const areaItem = this.props.areaItem;
    const areaType = this.props.areaType;
    const where = officePlanUtil.makeWhereForUnitsAssignedToArea(
      areaItem,
      areaType
    );
    queryCriteria.where = where;
  };

  onTabChange = (index) => {
    this.setState({ activeTabIndex: index });
  };

  popupPerson = (featureItem) => {
    const source = officePlanUtil.getPeopleSource();
    const attributes = featureItem.feature.attributes;
    const name = aiimUtil.getAttributeValue(
      attributes,
      FieldNames.PEOPLE_FULLNAME
    );
    Topic.publish(Topic.ShowItemPopup, {
      title: name,
      source: source,
      featureItem: featureItem,
      fetchFeatureItemGeometry: false,
      zoom: false,
      content: <PersonPopup sourceKey={source.key} featureItem={featureItem} />,
    });
  };

  popupUnit = (featureItem) => {
    const source = officePlanUtil.getUnitsSource();
    const attributes = featureItem.feature.attributes;
    const name = aiimUtil.getAttributeValue(attributes, FieldNames.NAME);
    Topic.publish(Topic.ShowItemPopup, {
      title: name,
      source: source,
      featureItem: featureItem,
      fetchFeatureItemGeometry: true,
      zoom: true,
      content: <UnitPopup sourceKey={source.key} featureItem={featureItem} />,
    });
  };

  queryPeopleCount() {
    const i18n = Context.instance.i18n;
    const source = sourceUtil.getPeopleSource();
    const queryCriteria = QueryCriteria.get(this.peopleQueryCriteriaKey);
    const options = {
      source: source,
      where: queryCriteria.where,
    };
    queryUtil
      .queryCount(options)
      .then((result) => {
        if (typeof result === "number") {
          let v = i18n.spaceplanner.people.peopleCountPattern;
          v = v.replace("{count}", result);
          if (this._mounted) this.setState({ peopleLabel: v });
        }
      })
      .catch((ex) => {
        console.error("Error querying people count", ex);
      });
  }

  queryUnitCount() {
    const i18n = Context.instance.i18n;
    const source = sourceUtil.getUnitsSource();
    const queryCriteria = QueryCriteria.get(this.unitsQueryCriteriaKey);
    const options = {
      source: source,
      where: queryCriteria.where,
    };
    queryUtil
      .queryCount(options)
      .then((result) => {
        if (typeof result === "number") {
          let v = i18n.spaceplanner.units.unitsCountPattern;
          v = v.replace("{count}", result);
          if (this._mounted) this.setState({ unitsLabel: v });
        }
      })
      .catch((ex) => {
        console.error("Error querying unit count", ex);
      });
  }

  render() {
    const i18n = Context.instance.i18n;
    const { activeTabIndex, peopleLabel, unitsLabel } = this.state;
    const areaType = this.props.areaType;
    let noPeople, noUnits;
    if (areaType === OfficePlan.SpaceAssignmentTypes.hotdesk) {
      noPeople = i18n.spaceplanner.hotdeskAreas.noPeople;
      noUnits = i18n.spaceplanner.hotdeskAreas.noUnits;
    } else {
      noPeople = i18n.spaceplanner.hotelAreas.noPeople;
      noUnits = i18n.spaceplanner.hotelAreas.noUnits;
    }

    return (
      <>
        <div style={{ marginLeft: "10px", marginRight: "10px" }}>
          <AreaStats
            areaItem={this.props.areaItem}
            areaType={this.props.areaType}
          />
        </div>
        <Tabs
          translucent
          activeTabIndex={activeTabIndex}
          onTabChange={this.onTabChange}
          style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <TabNav>
            <TabTitle key="people">{peopleLabel}</TabTitle>
            <TabTitle key="units">{unitsLabel}</TabTitle>
          </TabNav>
          <TabContents style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <TabSection
              key="people"
              style={{ padding: 0, background: "inherit", display: "flex", flexDirection: "column", flexGrow: 1 }}
            >
              <Items
                queryCriteriaKey={this.peopleQueryCriteriaKey}
                noResultsMessage={noPeople}
                disableForceUpdate={false}
                rdxFeatureItemsKey={null}
                renderItem={this.renderPerson}
                requeryOnPlanActions={[OfficePlan.Action_AssignmentsUpdated]}
                isShowAllButton={true}
              />
            </TabSection>
            <TabSection
              key="units"
              style={{ padding: 0, background: "inherit", display: "flex", flexDirection: "column", flexGrow: 1 }}
            >
              <Items
                queryCriteriaKey={this.unitsQueryCriteriaKey}
                noResultsMessage={noUnits}
                disableForceUpdate={false}
                rdxFeatureItemsKey={null}
                renderItem={this.renderUnit}
                requeryOnPlanActions={[OfficePlan.Action_AssignmentsUpdated]}
                isShowAllButton={true}
              />
            </TabSection>
          </TabContents>
        </Tabs>
      </>
    );
  }

  renderPerson = (source, featureItem, index) => {
    const i18n = Context.instance.i18n;
    const attributes = featureItem.feature.attributes;
    const name = aiimUtil.getAttributeValue(
      attributes,
      FieldNames.PEOPLE_FULLNAME
    );
    return (
      <ListItem
        key={featureItem.key}
        onClick={() => this.popupPerson(featureItem)}
      >
        <ListItemTitle>
          {name}
          <Button
            iconButton={true}
            icon={<DeleteIcon />}
            title={i18n.spaceplanner.unassign}
            onClick={(evt) => {
              this.confirmDelete(evt, featureItem, "person", index);
            }}
          />
        </ListItemTitle>
      </ListItem>
    );
  };

  renderUnit = (source, featureItem, index) => {
    const i18n = Context.instance.i18n;
    const attributes = featureItem.feature.attributes;
    const name = aiimUtil.getAttributeValue(attributes, FieldNames.NAME);
    return (
      <ListItem
        key={featureItem.key}
        onClick={() => this.popupUnit(featureItem)}
      >
        <ListItemTitle>
          {name}
          <Button
            iconButton={true}
            icon={<DeleteIcon />}
            title={i18n.spaceplanner.unassign}
            onClick={(evt) => {
              this.confirmDelete(evt, featureItem, "unit", index);
            }}
          />
        </ListItemTitle>
      </ListItem>
    );
  };
}

export default PeopleAndUnits;
